// src/components/DailyPlan/DailyPlan.js
import React, { useState, useEffect } from 'react';
import { Button, Form, ProgressBar, Alert } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import TextareaAutosize from 'react-autosize-textarea'; // 已正确导入 TextareaAutosize
import { useNavigate } from 'react-router-dom';
import { fetchDailyPlan, fetchGoals, fetchTasks, addTaskToDailyPlan, removeTaskFromDailyPlan, updateTaskNote, clearDailyPlans, updateDailySummary } from '../../apis/dailyPlanApi'; // 导入API服务
import { calculateTotalTime, getTotalElapsedTime, getTotalRemainingTime, getElapsedPercentage, formatTime, formatRemainingTime } from '../../utils/timeUtils'; // 导入工具函数
import TaskList from './TaskList'; // 导入任务列表组件
import styles from './DailyPlan.module.css';
import { sendToGPT } from '../../services/gptServiceES6'; // 导入GPT服务
import { sendMarkdownToWeChatBot } from '../../services/weChatBotServiceES6'; // 导入微信机器人服务

const DailyPlan = () => {
    const [dailyPlan, setDailyPlan] = useState(null);
    const [goals, setGoals] = useState([]);
    const [allGoals, setAllGoals] = useState([]);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());

    const [dailyGoal, setDailyGoal] = useState('');
    const [performance, setPerformance] = useState('');
    const [thoughts, setThoughts] = useState('');
    const [gains, setGains] = useState('');
    const [summary, setSummary] = useState('');

    const [gptResponse, setGptResponse] = useState('');
    const [loadingGpt, setLoadingGpt] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const loadDailyPlan = async () => {
            const plan = await fetchDailyPlan();
            setDailyPlan(plan);
            setDailyGoal(plan.dailyGoal || '');
            setPerformance(plan.dailySummary.performance || '');
            setThoughts(plan.dailySummary.thoughts || '');
            setGains(plan.dailySummary.gains || '');
            setSummary(plan.dailySummary.summary || '');
            const goalsData = await fetchGoals(plan);
            setGoals(goalsData);
            setAllGoals(goalsData);
        };

        loadDailyPlan();

        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleGoalSelect = async (selected) => {
        if (selected.length > 0) {
            setSelectedGoal(selected[0]);
            const tasksData = await fetchTasks(selected[0]._id);
            setTasks(tasksData);
        } else {
            setSelectedGoal(null);
            setTasks([]);
        }
    };

    const handleAddTask = async () => {
        if (selectedGoal && selectedTask) {
            const updatedPlan = await addTaskToDailyPlan(selectedGoal._id, selectedTask._id);
            setDailyPlan(updatedPlan);
            setSelectedGoal(null);
            setSelectedTask(null);
            const goalsData = await fetchGoals(updatedPlan);
            setGoals(goalsData);
        }
    };

    const handleRemoveTask = async (goalId, taskId) => {
        const updatedPlan = await removeTaskFromDailyPlan(goalId, taskId);
        setDailyPlan(updatedPlan);
        const goalsData = await fetchGoals(updatedPlan);
        setGoals(goalsData);
    };

    const handleNoteChange = async (goalId, taskId, value) => {
        await updateTaskNote(goalId, taskId, value);
        const goalsData = await fetchGoals(dailyPlan);
        setGoals(goalsData);
    };

    const handleClearDailyPlans = async () => {
        await clearDailyPlans();
        const plan = await fetchDailyPlan();
        setDailyPlan(plan);
    };

    const saveDailySummary = async () => {
        await updateDailySummary({ dailyGoal, performance, thoughts, gains, summary });
        // getGPTSuggestions();先关闭自动
    };

    const getGPTSuggestions = async () => {
        setLoadingGpt(true);
        try {
            const messagesList = [
                { isUser: true, content: thoughts },
                { isUser: true, content: gains },
                { isUser: true, content: summary },
            ];
            const gptResponse = await sendToGPT(messagesList);
            setGptResponse(gptResponse);
            // 将 GPT 响应发送到企业微信机器人
            await sendMarkdownToWeChatBot(`### GPT建议:\n${gptResponse}`);
        } finally {
            setLoadingGpt(false);
        }
    };

    const totalExpectedTime = dailyPlan ? calculateTotalTime(dailyPlan.selectedTasks, 'expectedTime') : 0;
    const totalInvestedTime = dailyPlan ? calculateTotalTime(dailyPlan.selectedTasks, 'investedTime') : 0;
    const totalRemainingTime = getTotalRemainingTime(currentTime);
    const elapsedPercentage = getElapsedPercentage(24 * 60 * 60, getTotalElapsedTime(currentTime));

    return (
        <div className={styles.container}>
            <h2 className="mb-4">每日计划</h2>
            <div className="mb-4">
                <div className="row">
                    <div className="col">
                        <p><strong>今日日期:</strong> {currentTime.toLocaleDateString()}</p>
                    </div>
                    <div className="col">
                        <p><strong>当前时刻:</strong> {formatTime(currentTime)}</p>
                    </div>
                    <div className="col">
                        <p><strong>今日剩余时间:</strong> {formatRemainingTime(totalRemainingTime)}</p>
                    </div>
                </div>
                <ProgressBar now={elapsedPercentage} label={`${elapsedPercentage.toFixed(2)}%`} />
            </div>
            <Form.Group className="mb-4">
                <Form.Label>
                    <strong>今日目标与规划:</strong>
                </Form.Label>
                <TextareaAutosize
                    className="form-control"
                    minRows={3}
                    value={dailyGoal}
                    onChange={(e) => setDailyGoal(e.target.value)}
                    onBlur={saveDailySummary}
                    style={{ resize: 'none' }} // 禁用手动调整
                />
            </Form.Group>
            <div className="task-summary mb-4">
                <div className="row">
                    <div className="col">
                        <p><strong>今日选择的任务累计预计投入时间:</strong> {totalExpectedTime} 分钟</p>
                    </div>
                    <div className="col">
                        <p><strong>今日选择的任务累计实际投入时间:</strong> {totalInvestedTime} 分钟</p>
                    </div>
                </div>
            </div>
            <div className={styles.wideSelector}>
                <Typeahead
                    id="goal-selector"
                    labelKey="name"
                    options={goals}
                    onChange={handleGoalSelect}
                    placeholder="选择目标"
                    selected={selectedGoal ? [selectedGoal] : []}
                    className={styles.typeaheadWide}
                />
            </div>
            {selectedGoal && (
                <div className={styles.wideSelector}>
                    <Typeahead
                        id="task-selector"
                        labelKey="task"
                        options={tasks}
                        onChange={(selected) => setSelectedTask(selected.length > 0 ? selected[0] : null)}
                        placeholder="选择任务"
                        selected={selectedTask ? [selectedTask] : []}
                        className={styles.typeaheadWide}
                    />
                </div>
            )}
            <Button variant="primary" onClick={handleAddTask} className="mb-4">添加任务</Button>
            <TaskList
                dailyPlan={dailyPlan}
                allGoals={allGoals}
                handleRemoveTask={handleRemoveTask}
                handleNoteChange={handleNoteChange}
            />
            <Form.Group className="mb-4">
                <Form.Label>
                    <strong>今日杰出表现:</strong>
                </Form.Label>
                <TextareaAutosize
                    className="form-control"
                    minRows={3}
                    value={performance}
                    onChange={(e) => setPerformance(e.target.value)}
                    onBlur={saveDailySummary}
                    style={{ resize: 'none' }}
                />
            </Form.Group>
            <Form.Group className="mb-4">
                <Form.Label>
                    <strong>今日思考:</strong>
                </Form.Label>
                <TextareaAutosize
                    className="form-control"
                    minRows={3}
                    value={thoughts}
                    onChange={(e) => setThoughts(e.target.value)}
                    onBlur={saveDailySummary}
                    style={{ resize: 'none' }}
                />
            </Form.Group>
            <Form.Group className="mb-4">
                <Form.Label>
                    <strong>今日收获:</strong>
                </Form.Label>
                <TextareaAutosize
                    className="form-control"
                    minRows={3}
                    value={gains}
                    onChange={(e) => setGains(e.target.value)}
                    onBlur={saveDailySummary}
                    style={{ resize: 'none' }}
                />
            </Form.Group>
            <Form.Group className="mb-4">
                <Form.Label>
                    <strong>今日总结:</strong>
                </Form.Label>
                <TextareaAutosize
                    className="form-control"
                    minRows={3}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    onBlur={saveDailySummary}
                    style={{ resize: 'none' }}
                />
            </Form.Group>
            {loadingGpt ? (
                <p>加载GPT建议中...</p>
            ) : (
                gptResponse && (
                    <Alert variant="success">
                        <strong>GPT建议:</strong> {gptResponse}
                    </Alert>
                )
            )}
        </div>
    );
};

export default DailyPlan;
